import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  Flex,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Pagination from '../../../components/Pagination';
import { ImportRowEditableStatuses, ImportRowStatuses, ImportStatuses } from '../../../constants/importStatuses';
import { showModal } from '../../../redux/modules/modal';
import { useImportRows } from '../../../services/import-service';

import EditImportRowModal from './EditImportRowModal';
import ImportStatusBadge from './ImportStatusBadge';
import { sortedIssues } from './util';

const STATUS_FILTERS = ['', ...Object.values(ImportRowStatuses)];

const LOCKED_IMPORT_STATUSES = [ImportStatuses.validating, ImportStatuses.running, ImportStatuses.rollingBack];

const effective = (row, field) => row.overrides?.[field] ?? row.resolved?.[field] ?? row.data?.[field] ?? '';

const ImportRowsTable = ({ importData }) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState('');
  const [pageNumber, setPageNumber] = useState(1);

  const { data, isLoading, isFetching } = useImportRows(importData.id, { status: status || null, pageNumber });

  const rows = data?.data ?? [];
  const importLocked = LOCKED_IMPORT_STATUSES.includes(importData.status);

  const handleEdit = row => {
    dispatch(showModal(EditImportRowModal, { title: `Row ${row.rowNumber}: ${row.filePath}`, size: 'xl' }, { importId: importData.id, row }));
  };

  return (
    <Box>
      <Flex alignItems="center" justifyContent="space-between" mb={3}>
        <Text textStyle="bodyLg500">{`Rows${data?.total !== undefined ? ` (${data.total})` : ''}`}</Text>
        <Select data-testid="row-status-filter" value={status} w="200px" onChange={event => { setStatus(event.target.value); setPageNumber(1); }}>
          {STATUS_FILTERS.map(option => (
            <option key={option || 'all'} value={option}>{option ? option.replace('_', ' ') : 'All statuses'}</option>
          ))}
        </Select>
      </Flex>

      {isLoading && <Text>Loading...</Text>}

      {!isLoading && rows.length === 0 && <Text>{status ? `No ${status} rows.` : 'No rows yet; upload and validate a manifest.'}</Text>}

      {rows.length > 0 && (
        <Box opacity={isFetching ? 0.6 : 1} overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>File</Th>
                <Th>Type</Th>
                <Th>File status</Th>
                <Th>Insured</Th>
                <Th>Project</Th>
                <Th>Status</Th>
                <Th>Issues</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {rows.map(row => {
                const issues = sortedIssues(row);
                const editable = !importLocked && ImportRowEditableStatuses.includes(row.status);
                const fileStatus = effective(row, 'fileStatus');

                return (
                  <Tr data-testid={`import-row-${row.id}`} key={row.id} verticalAlign="top">
                    <Td>{row.rowNumber}</Td>
                    <Td maxW="260px" wordBreak="break-all">{row.filePath}</Td>
                    <Td textTransform="capitalize">{effective(row, 'fileType') || '—'}</Td>
                    <Td>
                      {fileStatus
                        ? <Badge colorScheme={fileStatus === 'archived' ? 'gray' : 'green'} data-testid={`file-status-${row.id}`} variant="subtle">{fileStatus}</Badge>
                        : '—'}
                    </Td>
                    <Td>
                      {effective(row, 'insuredName') || '—'}
                      {row.resolved?.insuredMatch && (
                        <Text color="greyScale.500" textStyle="bodySm">{`match: ${row.resolved.insuredMatch}${row.insuredId ? ` #${row.insuredId}` : ''}`}</Text>
                      )}
                    </Td>
                    <Td>
                      {effective(row, 'project') || <Text color="greyScale.500">default</Text>}
                      {row.resolved?.record?.status === 'archived' && <Text color="greyScale.500" textStyle="bodySm">archived record</Text>}
                    </Td>
                    <Td><ImportStatusBadge kind="row" status={row.status} /></Td>
                    <Td maxW="360px">
                      {row.error && <Text color="red.600" textStyle="bodySm">{row.error}</Text>}
                      {issues.map(issue => (
                        <Text color={issue.level === 'error' ? 'red.600' : 'orange.600'} key={`${row.id}-${issue.code}-${issue.message}`} textStyle="bodySm">
                          {issue.message}
                        </Text>
                      ))}
                      {!row.error && issues.length === 0 && <Text color="greyScale.500" textStyle="bodySm">—</Text>}
                    </Td>
                    <Td>
                      {editable && (
                        <Button data-testid={`edit-row-${row.id}`} size="xs" variant="secondary" onClick={() => handleEdit(row)}>Edit</Button>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>

          {data?.lastPage > 1 && (
            <Pagination
              currentPage={data.currentPage}
              from={data.from}
              handlePageSelection={setPageNumber}
              pageCount={data.lastPage}
              perPage={data.perPage}
              to={data.to}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

ImportRowsTable.propTypes = { importData: PropTypes.object.isRequired };

export default ImportRowsTable;
